import { diffArrays, diffWordsWithSpace } from "diff";

export interface DiffPart {
  type: "added" | "removed" | "unchanged";
  text: string;
}

/**
 * Minimum word overlap (Dice coefficient) for a removed and an added line to
 * be shown as one edited line rather than as a whole removal and addition.
 */
const LINE_PAIRING_SIMILARITY_THRESHOLD = 0.5;

/**
 * Largest hunk, in removed lines times added lines, that still gets its lines
 * paired. Pairing costs that much time and memory, so a bigger hunk is shown
 * as whole removals and additions instead.
 */
const MAX_LINE_PAIRING_CANDIDATES = 10_000;

/** An edit followed by the unchanged run after it; any of the three may be empty. */
interface WordDiffBlock {
  removed: string;
  added: string;
  unchanged: string;
}

function appendPart(parts: DiffPart[], type: DiffPart["type"], text: string) {
  if (text === "") {
    return;
  }
  const lastPart = parts.at(-1);
  if (lastPart?.type === type) {
    lastPart.text += text;
  } else {
    parts.push({ type, text });
  }
}

/** Splits into lines that each keep their trailing newline. */
function splitLines(text: string): string[] {
  return text.match(/[^\n]*\n|[^\n]+/g) ?? [];
}

function countWords(line: string): Map<string, number> {
  const counts = new Map<string, number>();
  for (const word of line.split(/\s+/)) {
    if (word !== "") {
      counts.set(word, (counts.get(word) ?? 0) + 1);
    }
  }
  return counts;
}

/** Dice coefficient over two lines' word multisets; 0 when either is blank. */
function computeLineSimilarity(
  oldCounts: ReadonlyMap<string, number>,
  newCounts: ReadonlyMap<string, number>,
): number {
  let sharedCount = 0;
  let totalCount = 0;
  for (const [word, oldCount] of oldCounts) {
    sharedCount += Math.min(oldCount, newCounts.get(word) ?? 0);
    totalCount += oldCount;
  }
  for (const newCount of newCounts.values()) {
    totalCount += newCount;
  }
  return sharedCount === 0 ? 0 : (2 * sharedCount) / totalCount;
}

/**
 * Word diff of two similar lines. An unchanged run between two edits is folded
 * into them when it is no longer than the larger side of each neighboring
 * edit (diff-match-patch's semantic cleanup), so coincidental matches on small
 * words don't chop a rewritten phrase into per-word changes.
 */
function appendWordDiff(parts: DiffPart[], oldLine: string, newLine: string) {
  const blocks: WordDiffBlock[] = [];
  for (const change of diffWordsWithSpace(oldLine, newLine)) {
    let block = blocks.at(-1);
    const isEdit = change.added || change.removed;
    if (!block || (isEdit && block.unchanged !== "")) {
      block = { removed: "", added: "", unchanged: "" };
      blocks.push(block);
    }
    if (change.removed) {
      block.removed += change.value;
    } else if (change.added) {
      block.added += change.value;
    } else {
      block.unchanged += change.value;
    }
  }

  let index = 0;
  while (index < blocks.length - 1) {
    const block = blocks[index];
    const nextBlock = blocks[index + 1];
    if (!block || !nextBlock) {
      break;
    }
    const length = block.unchanged.length;
    if (
      length <= Math.max(block.removed.length, block.added.length) &&
      length <= Math.max(nextBlock.removed.length, nextBlock.added.length)
    ) {
      block.removed += block.unchanged + nextBlock.removed;
      block.added += block.unchanged + nextBlock.added;
      block.unchanged = nextBlock.unchanged;
      blocks.splice(index + 1, 1);
      // The grown edit can make the unchanged run before it eligible too.
      index = Math.max(index - 1, 0);
    } else {
      index++;
    }
  }

  for (const block of blocks) {
    appendPart(parts, "removed", block.removed);
    appendPart(parts, "added", block.added);
    appendPart(parts, "unchanged", block.unchanged);
  }
}

/**
 * Diffs one hunk of replaced lines. Lines are paired in order so that the
 * total similarity of the pairs is maximal; pairs get a word diff, and the
 * lines left over are removed or added whole.
 */
function appendHunkDiff(
  parts: DiffPart[],
  removedLines: readonly string[],
  addedLines: readonly string[],
) {
  if (removedLines.length * addedLines.length > MAX_LINE_PAIRING_CANDIDATES) {
    appendPart(parts, "removed", removedLines.join(""));
    appendPart(parts, "added", addedLines.join(""));
    return;
  }

  const addedWordCounts = addedLines.map(countWords);
  const similarities = removedLines.map((removedLine) => {
    const removedCounts = countWords(removedLine);
    return addedWordCounts.map((addedCounts) => {
      const similarity = computeLineSimilarity(removedCounts, addedCounts);
      return similarity >= LINE_PAIRING_SIMILARITY_THRESHOLD ? similarity : 0;
    });
  });

  // scores[i][j] is the best total similarity of removed lines i.. and added lines j..
  const scores = Array.from({ length: removedLines.length + 1 }, () =>
    new Array<number>(addedLines.length + 1).fill(0),
  );
  const readScore = (i: number, j: number) => scores[i]?.[j] ?? 0;
  const readPairScore = (i: number, j: number) => {
    const similarity = similarities[i]?.[j] ?? 0;
    return similarity > 0 ? similarity + readScore(i + 1, j + 1) : 0;
  };
  for (let i = removedLines.length - 1; i >= 0; i--) {
    for (let j = addedLines.length - 1; j >= 0; j--) {
      const row = scores[i];
      if (row) {
        row[j] = Math.max(
          readPairScore(i, j),
          readScore(i + 1, j),
          readScore(i, j + 1),
        );
      }
    }
  }

  // Unpaired lines between two pairs are held back so removals precede additions.
  let pendingRemoved = "";
  let pendingAdded = "";
  let i = 0;
  let j = 0;
  while (i < removedLines.length || j < addedLines.length) {
    const removedLine = removedLines[i];
    const addedLine = addedLines[j];
    const pairScore = readPairScore(i, j);
    if (
      removedLine !== undefined &&
      addedLine !== undefined &&
      pairScore > 0 &&
      pairScore === readScore(i, j)
    ) {
      appendPart(parts, "removed", pendingRemoved);
      appendPart(parts, "added", pendingAdded);
      pendingRemoved = "";
      pendingAdded = "";
      appendWordDiff(parts, removedLine, addedLine);
      i++;
      j++;
    } else if (
      removedLine !== undefined &&
      (addedLine === undefined || readScore(i + 1, j) === readScore(i, j))
    ) {
      pendingRemoved += removedLine;
      i++;
    } else {
      pendingAdded += addedLine ?? "";
      j++;
    }
  }
  appendPart(parts, "removed", pendingRemoved);
  appendPart(parts, "added", pendingAdded);
}

/**
 * Diff of two texts for display: line by line first, then word by word within
 * lines that were edited rather than rewritten. A rewritten line shows as one
 * removal and one addition, and an edit never matches words in another line.
 */
export function computeTextDiff(
  savedValue: string,
  editedValue: string,
): DiffPart[] {
  const parts: DiffPart[] = [];
  let removedLines: string[] = [];
  let addedLines: string[] = [];
  const lineChanges = diffArrays(
    splitLines(savedValue),
    splitLines(editedValue),
  );
  for (const lineChange of lineChanges) {
    if (lineChange.removed) {
      removedLines.push(...lineChange.value);
    } else if (lineChange.added) {
      addedLines.push(...lineChange.value);
    } else {
      appendHunkDiff(parts, removedLines, addedLines);
      removedLines = [];
      addedLines = [];
      appendPart(parts, "unchanged", lineChange.value.join(""));
    }
  }
  appendHunkDiff(parts, removedLines, addedLines);
  return parts;
}
